import { useMemo, useState } from 'react';

import type { OptionGroup } from '../types';

interface UseFilteredOptionsProps<T> {
  optionGroups: T[];
  searchValue?: string;
}

interface UseFilteredOptionsReturn<T> {
  filteredOptions: T[];
  onChangeSearchValue: (value: string) => void;
  searchValue: string;
}

export const useFilterByNameGroupOptions = <T>({
  optionGroups,
}: UseFilteredOptionsProps<T>): UseFilteredOptionsReturn<T> => {
  const [searchValue, setSearchValue] = useState<string>('');
  const toSearchableString = (value?: string) => (value ? String(value).toLowerCase() : '');

  const filteredOptions = useMemo(() => {
    const searchStr = toSearchableString(searchValue);
    if (!searchStr) return optionGroups;
    return optionGroups
      .map((group) => {
        const filteredInGroup = ((group as OptionGroup).options)
          .filter((opt) => Boolean(opt.label ?? opt.value))
          .filter((opt) => {
            const labelMatch = toSearchableString(opt.label).includes(searchStr);
            const valueMatch = toSearchableString(String(opt.value)).includes(searchStr);
            return labelMatch || valueMatch;
          });
        return filteredInGroup.length ? { ...group, options: filteredInGroup } : [];
      })
      .filter(Boolean);
  }, [optionGroups, searchValue]) as T[];

  return {
    filteredOptions,
    onChangeSearchValue: setSearchValue,
    searchValue,
  };
};
